import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  db, doc, getDoc, updateDoc, collection, addDoc, serverTimestamp,
} from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { notifyMaintenancePaid } from '../utils/notifications';
import { isTokenValid, msUntilExpiry, formatTimeLeft } from '../utils/shareTokens';
import {
  Sparkles, User, CreditCard, AlertCircle, Check, Loader2,
  Lock, Calendar,
} from 'lucide-react';

const PAYSTACK_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

const SharedMaintenancePage = () => {
  const { uid, scheduleId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);

  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (userData?.name && !payerName) setPayerName(userData.name);
    if (userData?.email && !payerEmail) setPayerEmail(userData.email);
  }, [userData]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) {
          setError('Missing payment token.');
          return;
        }
        const snap = await getDoc(doc(db, 'shareLinks', token));
        if (cancelled) return;
        if (!snap.exists()) {
          setError('This link is invalid or has been removed.');
          return;
        }
        const data = snap.data();
        if (data.type !== 'maintenance') {
          setError('This link is not a maintenance payment link.');
          return;
        }
        if (data.ownerUid !== uid || data.scheduleId !== scheduleId) {
          setError('This link does not match the routine.');
          return;
        }
        if (!isTokenValid(data)) {
          setError('This link has expired.');
          return;
        }
        if (data.paid) {
          setPaid(true);
        }
        setShare(data);
      } catch (err) {
        console.error('Failed to load shared routine:', err);
        if (!cancelled) setError('Something went wrong.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (uid && scheduleId) load();
    return () => { cancelled = true; };
  }, [uid, scheduleId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#EFA9B6]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={36} className="text-[#EFA9B6]" />
          </div>
          <h1 className="text-xl font-serif font-bold text-gray-900 mb-2">
            Link unavailable
          </h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Go to Skytone
          </button>
        </div>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={36} className="text-green-500" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
            Paid — thank you! 💫
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {(share.ownerName || 'They').split(' ')[0]} has been notified that their routine was covered.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Explore Skytone
          </button>
        </div>
      </div>
    );
  }

  const { routineTitle, amount, ownerName, ownerImage, preferredProName, category } = share;
  const timeLeft = formatTimeLeft(msUntilExpiry(share));
  const firstName = (ownerName || 'A Skytone user').split(' ')[0];

  const handlePaystackCheckout = () => {
    if (!payerName.trim()) return alert('Please enter your name.');
    if (!payerEmail.trim() || !payerEmail.includes('@')) return alert('Please enter a valid email.');
    if (!PAYSTACK_KEY) {
      alert('Payment is not configured.');
      return;
    }

    setPaying(true);

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_KEY,
      email: payerEmail.trim(),
      amount: Math.round(Number(amount || 0) * 100),
      currency: 'ZAR',
      metadata: {
        custom_fields: [
          { display_name: 'Routine', variable_name: 'routine', value: routineTitle },
          { display_name: 'Paid by', variable_name: 'paid_by', value: payerName },
          { display_name: 'For', variable_name: 'for', value: ownerName },
        ],
      },
      callback: (response) => {
        finalizePayment(response.reference);
      },
      onClose: () => setPaying(false),
    });

    handler.openIframe();
  };

  const finalizePayment = async (reference) => {
    try {
      // 1. Mark share doc as paid (guest-writable per rules)
      await updateDoc(doc(db, 'shareLinks', token), {
        paid: true,
        paidBy: {
          name: payerName.trim(),
          email: payerEmail.trim(),
          userId: user?.uid || null,
          isGuest: !user,
        },
        paidAt: serverTimestamp(),
        paymentReference: reference,
        appliedToSchedule: false, // owner's HomePage will mirror it
      });

      // 2. Notify owner
      try {
        await notifyMaintenancePaid({
          ownerId: share.ownerUid,
          payerName: payerName.trim(),
          routineTitle,
          amount,
          scheduleId,
        });
      } catch (e) {
        console.warn('Notify failed:', e);
      }

      setPaid(true);
      setPaying(false);
    } catch (err) {
      console.error('Finalize failed:', err);
      alert('Payment received but we could not save your update. Contact support with reference: ' + reference);
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCF8F9] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[#EFA9B6] font-bold tracking-widest text-xs uppercase">
            <Sparkles size={14} />
            Skytone
          </div>
        </div>

        {/* Owner header */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#FFF5F6] overflow-hidden flex items-center justify-center shrink-0">
              {ownerImage ? (
                <img src={ownerImage} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={24} className="text-[#EFA9B6]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-0.5">
                Help with a routine
              </p>
              <h1 className="text-xl font-serif font-bold text-gray-900 truncate">
                {firstName}'s routine
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">{timeLeft}</p>
            </div>
          </div>
        </div>

        {/* Routine summary */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            Routine
          </h2>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#FFF5F6] flex items-center justify-center shrink-0">
              <Calendar size={20} className="text-[#EFA9B6]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 truncate">{routineTitle}</p>
              {category && (
                <p className="text-xs text-gray-400 capitalize">{category}</p>
              )}
              {preferredProName && (
                <p className="text-xs text-gray-500 mt-1">
                  Preferred pro: {preferredProName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Amount */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <div className="flex justify-between items-center">
            <span className="font-bold text-gray-900">Amount due</span>
            <span className="font-bold text-[#EFA9B6] text-2xl">
              R{Number(amount || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Payer info */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            Your details
          </h2>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Your name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 outline-none text-sm"
            />
            <input
              type="email"
              placeholder="Your email (for receipt)"
              value={payerEmail}
              onChange={(e) => setPayerEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 outline-none text-sm"
            />
            {!user && (
              <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <Lock size={11} />
                No account needed — we'll email your receipt.
              </p>
            )}
          </div>
        </div>

        {/* Pay button */}
        <button
          onClick={handlePaystackCheckout}
          disabled={paying || !payerName.trim() || !payerEmail.includes('@')}
          className="w-full bg-[#EFA9B6] text-white py-4 rounded-full font-bold text-sm shadow-lg hover:bg-[#EFA9B6]/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {paying ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Processing…
            </>
          ) : (
            <>
              <CreditCard size={18} />
              Pay R{Number(amount || 0).toFixed(2)} for {firstName}
            </>
          )}
        </button>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Secured by Paystack
        </p>
      </div>
    </div>
  );
};

export default SharedMaintenancePage;